import { forwardRef } from 'react';

import { Link } from '@remix-run/react';
import type { RemixLinkProps } from '@remix-run/react/dist/components';
import cx from 'classnames';

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  type?: 'button' | 'submit' | 'reset';
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'tertiary'
    | 'overide'
    | 'danger'
    | 'danger-light'
    | 'underline'
    | 'flush';
  btnSize?: 'sm' | 'md'; // Size is already a native prop, so we can't use it
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  isLoading?: boolean;
  block?: boolean;
  disabled?: boolean;
  linkProps?: RemixLinkProps;
  style?: React.CSSProperties;
}

const BASE_CLASSES =
  'border font-medium shadow-sm wt-button-font inline-flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 justify-center transition-colors';

const PRIMARY_CLASSES =
  'border-wt-primary text-wt-text-on-primary bg-wt-primary disabled:border-gray-500 focus:ring-wt-primary';

const SECONDARY_CLASSES =
  'bg-white hover:bg-gray-100 text-gray-800 border-gray-200 disabled:bg-gray-200 disabled:text-gray-300 focus:ring-wt-secondary';

const GHOST_CLASSES = 'border-gray-300 text-gray-700 bg-white shadow-md focus:ring-wt-primary';

const DANGER_CLASSES =
  'border-red-600 text-white bg-red-600 shadow-md hover:border-red-700 hover:bg-red-700 focus:border-red-800 focus:bg-red-800 focus:ring-red-800';

const DANGER_LIGHT_CLASSES =
  'border-gray-200 text-red-600 bg-gray-50 shadow-md hover:border-gray-300 hover:bg-gray-100 focus:border-gray-300 focus:bg-gray-200 focus:ring-gray-300';

const UNDERLINE_CLASSES = 'border-transparent text-gray-700 bg-white underline';

const TERTIARY_CLASSES =
  'border-wt-text-on-tertiary text-wt-text-on-tertiary bg-transparent focus:ring-wt-text-on-tertiary shadow-md';

const FLUSH_CLASSES = 'border-transparent text-gray-700 bg-transparent hover:bg-gray-100';

const SMALL_CLASSES = 'px-2 py-1 text-sm';

const MEDIUM_CLASSES = 'px-4 py-2 text-sm';

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      type,
      children,
      variant = 'primary',
      onClick,
      className,
      btnSize = 'md',
      isLoading = false,
      block = false,
      disabled = false,
      linkProps,
      style = {},
    },
    ref,
  ) => {
    const classes = cx(
      BASE_CLASSES,
      'rounded-wt',
      btnSize === 'sm' ? SMALL_CLASSES : MEDIUM_CLASSES,
      variant === 'primary' ? PRIMARY_CLASSES : '',
      variant === 'secondary' ? SECONDARY_CLASSES : '',
      variant === 'ghost' ? GHOST_CLASSES : '',
      variant === 'tertiary' ? TERTIARY_CLASSES : '',
      variant === 'danger' ? DANGER_CLASSES : '',
      variant === 'danger-light' ? DANGER_LIGHT_CLASSES : '',
      variant === 'underline' ? UNDERLINE_CLASSES : '',
      variant === 'flush' ? FLUSH_CLASSES : '',
      isLoading ? 'disabled:cursor-not-allowed opacity-60 border-transparent' : '',
      disabled ? 'disabled:cursor-not-allowed border-transparent' : '',
      block ? 'w-full' : '',
      className,
    );

    if (linkProps) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={linkProps?.to}
          prefetch={linkProps?.prefetch || 'none'}
          className={classes}
          style={style}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        type={type}
        className={classes}
        disabled={disabled}
        style={style}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button'; // Optional: helps with debugging

export default Button;
